import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { userEndpoint, Base_URL } from "actions/API_endpoint";
import Swal from "sweetalert2";

export default function ManageMembers() {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const fetchMembers = async () => {
        try {
            const response = await fetch(`${Base_URL}${userEndpoint.getAllMembers}`);
            const data = await response.json();
            if (data.success) {
                setMembers(data.data);
            }
        } catch (error) {
            console.error("Error fetching members:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMembers();
    }, []);

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You will not be able to recover this member!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        });

        if (result.isConfirmed) {
            try {
                const response = await fetch(`${Base_URL}${userEndpoint.deleteMember(id)}`, {
                    method: "DELETE"
                });
                const data = await response.json();

                if (data.success) {
                    Swal.fire("Deleted!", "Member has been deleted.", "success");
                    fetchMembers();
                } else {
                    Swal.fire("Error!", data.error || "Failed to delete member.", "error");
                }
            } catch (error) {
                console.error("Error deleting member:", error);
                Swal.fire("Error!", "Failed to delete member.", "error");
            }
        }
    };

    return (
        <div className="flex flex-wrap mt-4">
            <div className="w-full mb-12 px-4">
                <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-dark-secondary text-white">
                    <div className="rounded-t mb-0 px-4 py-3 border-0">
                        <div className="flex flex-wrap items-center justify-between">
                            <div className="relative w-full px-4 max-w-full flex-grow flex-1">
                                <h3 className="font-semibold text-lg text-white">
                                    Manage Members
                                </h3>
                            </div>
                            <div className="relative w-full px-4 max-w-full flex-grow flex-1 text-right">
                                <button
                                    className="bg-accent-primary text-white active:bg-accent-hover font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
                                    type="button"
                                    onClick={() => navigate("/admin/createMember")}
                                >
                                    <i className="fas fa-plus mr-2"></i> Add Member
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="block w-full overflow-x-auto min-h-[500px]">
                        {loading ? (
                            <div className="flex justify-center items-center h-64">
                                <div className="w-12 h-12 border-4 border-accent-primary border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        ) : (
                            <table className="items-center w-full bg-transparent border-collapse">
                                <thead>
                                    <tr>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-dark-tertiary text-gray-300 border-dark-tertiary">
                                            Image
                                        </th>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-dark-tertiary text-gray-300 border-dark-tertiary">
                                            Name
                                        </th>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-dark-tertiary text-gray-300 border-dark-tertiary">
                                            Designation
                                        </th>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-dark-tertiary text-gray-300 border-dark-tertiary">
                                            Type
                                        </th>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-dark-tertiary text-gray-300 border-dark-tertiary">
                                            Sort Order
                                        </th>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-dark-tertiary text-gray-300 border-dark-tertiary">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {members.map((member) => (
                                        <tr key={member.id} className="border-b border-dark-tertiary hover:bg-dark-tertiary/50">
                                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm whitespace-nowrap p-4">
                                                {member.image_url ? (
                                                    <img
                                                        src={`${process.env.REACT_APP_API_URL || Base_URL}/uploads/${member.image_url}`}
                                                        alt={member.name}
                                                        className="w-12 h-12 rounded-full object-cover border-2 border-dark-tertiary"
                                                        onError={(e) => { e.target.src = "https://via.placeholder.com/50"; }}
                                                    />
                                                ) : (
                                                    <div className="w-12 h-12 rounded-full bg-dark-tertiary flex items-center justify-center">
                                                        <i className="fas fa-user text-gray-500"></i>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm whitespace-nowrap p-4 font-bold text-white">
                                                {member.name}
                                            </td>
                                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm whitespace-nowrap p-4 text-gray-300">
                                                {member.designation}
                                            </td>
                                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm whitespace-nowrap p-4 text-gray-300">
                                                <span className={`px-2 py-1 rounded text-xs font-bold ${member.type === 'executive team' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'
                                                    }`}>
                                                    {member.type}
                                                </span>
                                            </td>
                                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm whitespace-nowrap p-4 text-gray-300">
                                                {member.prefrence_no}
                                            </td>
                                            <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm whitespace-nowrap p-4 text-right">
                                                <button
                                                    className="text-blue-400 hover:text-blue-300 mr-4"
                                                    onClick={() => navigate(`/admin/editMember/${member.id}`)}
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button
                                                    className="text-red-400 hover:text-red-300"
                                                    onClick={() => handleDelete(member.id)}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {members.length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="text-center py-8 text-gray-400">
                                                No members found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
